import BaseStorage from "./BaseStorage";

/**
 * Configuracion de visibilidad de los elementos del mapa.
 *
 * @see VisibilityService
 */
export interface VisibilityConfig {
    showHotels: boolean;
    showPOIs: boolean;
}

/**
 * Seguimiento de la peticion de valoracion de la app.
 *
 * @see RateAppService
 */
export interface RateAppData {
    /** Fechas consecutivas (YYYY-MM-DD) en las que se abrio la app. */
    dates: string[];
    /** True si el usuario ya valoro o descarto dos veces el recordatorio. */
    rated: boolean;
    /** Fecha en la que volver a mostrar el dialogo tras el primer descarte. */
    deferredReviewDate: string | null;
}

/**
 * Storage service for App data.
 */
class AppStorage extends BaseStorage {

    // All names like 'user.xxx'
    static keyPrefix = 'app';

    /**
     * Gets storage object.
     */
    static storage(): Storage {
      return window.localStorage;
    }

    /**
     * Gets intalled version.
     */
    static getInstalledVersion(): string | null {
      return this.getItem('install_version');
    }

    /**
     * Sets intalled version.
     */
    static setInstalledVersion(version: string): void {
      this.setItem('install_version', version);
    }

    /**
     * Gets the published version whose update notice was already shown.
     */
    static getUpdateNoticeVersion(): string | null {
      return this.getItem('update_notice_version');
    }

    /**
     * Sets the published version whose update notice has just been shown.
     */
    static setUpdateNoticeVersion(version: string): void {
      this.setItem('update_notice_version', version);
    }

    /**
     * Gets intalled date UTC.
     */
    static getInstallDate(): string {
      const installed = this.getItem('install_date');
      return installed ? (new Date(parseInt(installed))).toLocaleString() : '';
    }

    /**
     * Sets install date, save as timestamp.
     */
    static setInstallDate(): void {
      this.setItem('install_date', Date.now());
    }

    /**
     * Remove all items in localStorage.
     */
    static reset(): void {
        sessionStorage.clear()
        this.storage().clear();
    }


    /**
     * Gets app is opened for first time.
     */
    static getOpenedBefore(): boolean {
        return this.getItem('opened_before') !== null &&
            this.getItem('opened_before') !== 'false';
    }

    /**
     * Sets app is opened for first time.
     *
     */
    static setOpenedBefore(value: boolean = true): void {
        this.setItem('opened_before', value);
    }

    /**
     * Gets if direction has been clicked.
     */
    static getDirectionClicked(): boolean {
        const clicked = this.getItem('direction_clicked');
        return clicked === 'true';
    }

    /**
     * Sets if direction has been clicked
     */
    static setDirectionClicked(value: boolean): void {
        this.setItem('direction_clicked', value);
    }

    /**
     * Gets visibility configuration object.
     */
    static getVisibilityConfig(): VisibilityConfig {
        const config = this.getObject<VisibilityConfig>('visibility_config');
        if (config) {
            return config;
        }

        // Valores por defecto
        return {
            showHotels: true,
            showPOIs: true
        };
    }

    /**
     * Sets visibility configuration object.
     */
    static setVisibilityConfig(config: VisibilityConfig): void {
        this.setObject('visibility_config', config);
    }

    /**
     * Gets the rate app tracking data object.
     *
     * Lo almacenado es JSON sin validar, de ahi el Record<string, unknown>: los
     * tres campos se comprueban uno a uno antes de darlos por buenos.
     */
    static getRateAppData(): RateAppData {
        const data: Record<string, unknown> = this.getObject<Record<string, unknown>>('rate_app_data') || {};

        return {
            dates: Array.isArray(data.dates) ? data.dates as string[] : [],
            rated: data.rated === true,
            deferredReviewDate: typeof data.deferredReviewDate === 'string' ? data.deferredReviewDate : null,
        };
    }

    /**
     * Sets the rate app tracking data object.
     */
    static setRateAppData(data: RateAppData): void {
        this.setObject('rate_app_data', data);
    }
}

export default AppStorage;
